import { Answer } from "models/Answer";
import {
    QuestionConditionalGroupType,
    QuestionDependencyCondition,
    QuestionDependencyQuestionAnswerOperator,
    QuestionDependencyType,
} from "models/Question";

type DependencyQuestion = {
    id: string;
    dependencies?: {
        conditions: QuestionDependencyCondition[];
    };
};

type DependencyCheckParams = {
    condition: QuestionDependencyCondition;
    answers: Answer[];
    moduleInstanceId: string;
    getModuleInstanceIdForAnswer: (answer: Answer) => string | undefined;
    isDependencyQuestionVisible: (questionId: string) => boolean;
};

function isConditionMet({
    condition,
    answers,
    moduleInstanceId,
    getModuleInstanceIdForAnswer,
    isDependencyQuestionVisible,
}: DependencyCheckParams): boolean {
    switch (condition.type) {
        case QuestionDependencyType.QuestionAnswer: {
            if (!isDependencyQuestionVisible(condition.dependsOnQuestionId)) {
                return false;
            }

            const answer = answers.find(
                (a) =>
                    a.questionId === condition.dependsOnQuestionId &&
                    getModuleInstanceIdForAnswer(a) === moduleInstanceId,
            );

            if (!answer) {
                return false;
            }

            switch (condition.op) {
                case QuestionDependencyQuestionAnswerOperator.Contains:
                    return answer.activeAnswerTags.some(
                        (tag) => tag.tag?.value === condition.value,
                    );
                case QuestionDependencyQuestionAnswerOperator.IsAnswered:
                    return !!answer.activeAnswerTags.length;
                default:
                    return false;
            }
        }
        case QuestionDependencyType.ConditionalGroup:
            switch (condition.conditionalGroupType) {
                case QuestionConditionalGroupType.And:
                    return condition.conditions.every((subCondition) =>
                        isConditionMet({
                            condition: subCondition,
                            answers,
                            moduleInstanceId,
                            getModuleInstanceIdForAnswer,
                            isDependencyQuestionVisible,
                        }),
                    );
                case QuestionConditionalGroupType.Or:
                    return condition.conditions.some((subCondition) =>
                        isConditionMet({
                            condition: subCondition,
                            answers,
                            moduleInstanceId,
                            getModuleInstanceIdForAnswer,
                            isDependencyQuestionVisible,
                        }),
                    );
                default:
                    return false;
            }
        default:
            return false;
    }
}

type DependencyMatchParams<TQuestion extends DependencyQuestion> = {
    question: TQuestion;
    answers: Answer[] | undefined;
    getModuleInstanceIdForQuestion: (question: TQuestion) => string;
    getModuleInstanceIdForAnswer: (answer: Answer) => string | undefined;
    isDependencyQuestionVisible?: (questionId: string) => boolean;
};

export function areQuestionDependenciesMet<
    TQuestion extends DependencyQuestion,
>({
    question,
    answers,
    getModuleInstanceIdForQuestion,
    getModuleInstanceIdForAnswer,
    isDependencyQuestionVisible = () => true,
}: DependencyMatchParams<TQuestion>): boolean {
    const conditions = question.dependencies?.conditions;
    if (!conditions?.length) {
        return true;
    }
    if (!answers?.length) {
        return false;
    }

    const moduleInstanceId = getModuleInstanceIdForQuestion(question);
    return conditions.every((condition) =>
        isConditionMet({
            condition,
            answers,
            moduleInstanceId,
            getModuleInstanceIdForAnswer,
            isDependencyQuestionVisible,
        }),
    );
}

type BuildVisibilityMapParams<TQuestion extends DependencyQuestion> = {
    questions: TQuestion[];
    answers: Answer[] | undefined;
    getModuleInstanceIdForQuestion: (question: TQuestion) => string;
    getModuleInstanceIdForAnswer: (answer: Answer) => string | undefined;
    canRenderQuestion?: (question: TQuestion) => boolean;
};

export function buildQuestionVisibilityMap<
    TQuestion extends DependencyQuestion,
>({
    questions,
    answers,
    getModuleInstanceIdForQuestion,
    getModuleInstanceIdForAnswer,
    canRenderQuestion = () => true,
}: BuildVisibilityMapParams<TQuestion>): Map<string, boolean> {
    //using map for performace reasons
    const questionById = new Map(
        questions.map((question) => [question.id, question]),
    );
    const visibilityMap = new Map<string, boolean>();
    const visiting = new Set<string>();

    const evaluateQuestionVisibility = (question: TQuestion): boolean => {
        if (visibilityMap.has(question.id)) {
            return visibilityMap.get(question.id) as boolean;
        }

        if (visiting.has(question.id)) {
            return false;
        }

        visiting.add(question.id);

        const isVisible =
            canRenderQuestion(question) &&
            areQuestionDependenciesMet({
                question,
                answers,
                getModuleInstanceIdForQuestion,
                getModuleInstanceIdForAnswer,
                isDependencyQuestionVisible: (questionId) => {
                    const dependencyQuestion = questionById.get(questionId);
                    if (!dependencyQuestion) {
                        return false;
                    }
                    return evaluateQuestionVisibility(dependencyQuestion);
                },
            });

        visiting.delete(question.id);
        visibilityMap.set(question.id, isVisible);
        return isVisible;
    };

    questions.forEach((question) => {
        evaluateQuestionVisibility(question);
    });

    return visibilityMap;
}
